import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Globe } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { References } from "@/components/References";
import { Reveal, Overline } from "@/components/Reveal";
import { SectionBackdrop } from "@/components/SectionBackdrop";
import { KEY_FIGURES, DIRECTORS } from "@/data/content";
import { useLang, MANIFESTO_PARAS_EN } from "@/i18n";

const MANIFESTO_PARAS = [
  "NEW CREATION studio est un studio de création artistique pluridisciplinaire qui transforme les objets, les espaces et les événements en expériences visuelles uniques.",
  "À la croisée de l'art, de la personnalisation et de l'événementiel, nous réunissons des artistes aux savoir-faire complémentaires pour imaginer et réaliser des créations sur mesure, fortes et singulières, adaptées à chaque projet.",
  "Notre expérience nous a conduits à collaborer avec des acteurs majeurs tels que Adidas, POSCA, Galeries Lafayette Paris, La Samaritaine, Printemps, Citadium, JD, W Hotels, Le Royal Monceau – Raffles Paris, Quai 54, Palais des Congrès de Paris, BRED Banque Populaire, Fouquet's Paris et Festival de Cannes.",
  "Avec plus de 5 000 projets de personnalisation réalisés, ainsi que de nombreuses fresques, œuvres et expériences artistiques, NEW CREATION met la création au service de projets qui souhaitent marquer les esprits, créer de l'engagement et donner une nouvelle dimension à leur image.",
];

const FIG_KEYS = ["st.fig1", "st.fig2", "st.fig3", "st.fig4"];

export default function LeStudio() {
  const { t, lang } = useLang();
  const paras = lang === "en" ? MANIFESTO_PARAS_EN : MANIFESTO_PARAS;

  return (
    <div>
      <Nav />
      <main className="pt-36 pb-0">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 overflow-hidden">
          <SectionBackdrop images={["/works/work-1.jpg", "/works/work-3.jpg"]} opacity={0.14} veil="strong" />
          <Reveal>
            <Overline>{t("ov.studio")}</Overline>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter uppercase mb-16">
              {t("st.h1a")}<br />
              <span className="text-gold italic normal-case">{t("st.h1b")}</span>
            </h1>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-10 mb-28" data-testid="manifesto">
            <Reveal className="lg:col-span-4">
              <Overline>{t("ov.manifeste")}</Overline>
            </Reveal>
            <div className="lg:col-span-8 space-y-8">
              {paras.map((para, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p className={`leading-relaxed ${i === 0 ? "font-serif text-2xl lg:text-3xl font-light text-white" : "text-white/60"}`}>
                    {para}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-2 lg:grid-cols-4 gap-10" data-testid="key-figures">
            {KEY_FIGURES.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.06}>
                <p className="font-serif text-5xl lg:text-6xl font-light text-gold leading-none mb-3">{f.value}</p>
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/50">{t(FIG_KEYS[i])}</p>
              </Reveal>
            ))}
          </div>
        </div>

        <References full />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <Reveal>
            <Overline>{t("ov.team")}</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight mb-16">
              {t("st.teamH2")}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {DIRECTORS.map((d, i) => (
              <Reveal key={d.handle} delay={i * 0.1}>
                <div className="beam-card" data-testid={`director-card-${d.handle.slice(1)}`}>
                  <div className="beam-inner p-10 lg:p-14 group">
                    <div className="w-20 h-20 border border-gold/40 flex items-center justify-center mb-8 transition-colors duration-500 group-hover:bg-gold/10">
                      <span className="font-serif text-3xl text-gold">{d.name[0]}</span>
                    </div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-2">
                      {lang === "en" ? t(d.handle === "@artwhos" ? "st.role1" : "st.role2") : d.role}
                    </p>
                    <h3 className="font-serif text-3xl font-light mb-4">{d.name}</h3>
                    <div className="flex flex-wrap gap-2 mb-6">
                      <a href={`https://instagram.com/${d.handle.slice(1)}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`director-instagram-${d.handle.slice(1)}`}
                        className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-gold hover:border-gold transition-colors duration-300">
                        <Instagram size={13} /> {d.handle}
                      </a>
                      <a href={d.siteUrl} target="_blank" rel="noopener noreferrer"
                        data-testid={`director-site-${d.handle.slice(1)}`}
                        className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-white/60 hover:border-gold hover:text-gold transition-colors duration-300">
                        <Globe size={13} /> {d.site}
                      </a>
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed">
                      {lang === "en" ? t(d.handle === "@artwhos" ? "st.bio1" : "st.bio2") : d.bio}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-24 border-t border-white/10 pt-16">
            <h2 className="font-serif text-3xl lg:text-4xl font-light mb-8">
              {t("st.ctaH2")}
            </h2>
            <div className="flex flex-wrap gap-4">
              <Link to="/prestations" data-testid="studio-cta-prestations"
                className="bg-gold text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light">
                {t("hero.cta1")}
              </Link>
              <Link to="/contact" data-testid="studio-cta-contact"
                className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-4 text-sm uppercase tracking-[0.15em] transition-colors duration-300 hover:border-gold hover:text-gold">
                {t("st.cta2")} <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
